/* eslint-disable react/no-array-index-key */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { palette } from '@m12s/component-library';
import moment from 'moment';
import { useSelector } from 'react-redux';
import {
  getCurrentWorkOrderOp,
  getCurrentWorkOrderOpTimeToComplete,
} from 'lib/selectors/getCurrentWorkOrderOp';

import useFetchJobSubmittedParts from 'lib/hooks/useFetchJobSubmittedParts';
import { getSelectWorkOrderOpOptions } from 'lib/selectors/getSelectWorkOrderOp';
import { formatValue } from 'lib/utils/text';
import WorkOrderOpMachineName from 'components/WorkOrderOp/MachineName';
import { getRemainingOperationDuration } from './helpers';

const WorkOrderBodyContainer = styled.div`
  white-space: nowrap;

  ${({ isSearchMode }) => {
    if (isSearchMode) {
      return `
        display: grid;
        gap: 1rem;
        grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
        align-items: center;
      `;
    }

    return `
      display: flex;
      flex-direction: column;
      gap: 0.625rem;
    `;
  }}
`;

const StatHeading = styled.div`
  font-size: 0.875rem;
  line-height: 1rem;
  font-weight: 400;
  color: ${palette.Grey700};

  ${({ isSearchMode }) => {
    return isSearchMode && `font-size: 0.625rem;`;
  }}
`;

const StatValue = styled.div`
  font-size: 0.875rem;
  line-height: 1.094rem;
  font-weight: 600;
  color: ${palette.Grey700};

  ${({ isSearchMode }) => {
    return (
      isSearchMode &&
      `
      font-size: 0.625rem;
      line-height: 0.625rem;
      `
    );
  }}
`;

const BottomText = styled.div`
  font-size: 2rem;
  line-height: 2rem;
  font-weight: 600;
  color: ${palette.Grey700};
  margin-top: auto;
`;

const Stat = ({ label, value, isSearchMode }) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: isSearchMode ? '0rem' : '.25rem',
      }}
    >
      <StatHeading isSearchMode={isSearchMode}>{label}</StatHeading>
      <StatValue isSearchMode={isSearchMode}>{value}</StatValue>
    </div>
  );
};

const RootContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: ${({ isSearchMode }) => {
    return isSearchMode ? '0.5rem 0.75rem' : '1rem';
  }};
  flex-grow: 1;
`;

Stat.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.node,
  ]),
};

const WorkOrderBody = ({ workOrderOp, active }) => {
  const currentWorkOrderOp = useSelector(getCurrentWorkOrderOp);
  const { isSearchMode } = useSelector(getSelectWorkOrderOpOptions);
  const currentWorkOrderOpTimeToComplete = useSelector(
    getCurrentWorkOrderOpTimeToComplete
  );

  const {
    scheduledStartDate,
    finishQuantity,
    startAt,
    workOrder,
    startEstimatedDateOfCompletion,
    cycleTimeMs,
  } = workOrderOp;

  const remainingOperationDuration = getRemainingOperationDuration({
    operation: workOrderOp,
    currentOperation: currentWorkOrderOp,
    currentWorkOrderOpTimeToComplete:
      active && currentWorkOrderOpTimeToComplete,
  });

  const [requestJobSubmittedParts, { parts }] = useFetchJobSubmittedParts();

  useEffect(() => {
    if (workOrderOp) {
      requestJobSubmittedParts(workOrderOp);
    }
  }, [requestJobSubmittedParts, workOrderOp]);

  const orderProgressText = [parts?.totalParts, finishQuantity]
    .map((data) => {
      return formatValue(data)?.toLocaleString() || '-';
    })
    .join(' / ');

  if (!workOrderOp) {
    return null;
  }

  const durationText =
    moment
      .duration(remainingOperationDuration)
      .format('h[h] m[m] s[s]', { largest: 2 }) || '0s';

  const elements = {
    dueDate: (
      <Stat
        isSearchMode={isSearchMode}
        label="Order due date"
        value={
          workOrder?.dueDate ? moment(workOrder.dueDate).format('L') : 'N/A'
        }
      />
    ),

    plannedStartDate: active ? (
      <Stat
        isSearchMode={isSearchMode}
        label="Planned start date"
        value={moment(startAt).format('L')}
      />
    ) : (
      <Stat
        isSearchMode={isSearchMode}
        label="Planned start date"
        value={
          startEstimatedDateOfCompletion
            ? moment(startEstimatedDateOfCompletion).format('L')
            : scheduledStartDate
            ? moment(scheduledStartDate).format('L')
            : 'N/A'
        }
      />
    ),

    estDuration: (
      <Stat
        isSearchMode={isSearchMode}
        label="Earliest est. complete"
        value={cycleTimeMs ? durationText || 'N/A' : 'No cycle time'}
      />
    ),

    quantityRequired: (
      <Stat
        isSearchMode={isSearchMode}
        label="Quantity Required"
        value={orderProgressText}
      />
    ),

    resource: (
      <Stat
        isSearchMode={isSearchMode}
        label="Resource"
        value={<WorkOrderOpMachineName workOrderOp={workOrderOp} />}
      />
    ),
  };

  const elementsOrdered = isSearchMode
    ? [
        elements.dueDate,
        elements.plannedStartDate,
        elements.estDuration,
        elements.quantityRequired,
        elements.resource,
      ]
    : [
        elements.dueDate,
        elements.plannedStartDate,
        elements.estDuration,
        elements.resource,
      ];

  return (
    <RootContainer isSearchMode={isSearchMode}>
      <WorkOrderBodyContainer isSearchMode={isSearchMode}>
        {elementsOrdered.map((element, index) => {
          return (
            <React.Fragment key={`workorder-stat-${index}`}>
              {element}
            </React.Fragment>
          );
        })}
      </WorkOrderBodyContainer>
      {!isSearchMode && <BottomText>{orderProgressText}</BottomText>}
    </RootContainer>
  );
};

WorkOrderBody.propTypes = {
  workOrderOp: PropTypes.object,
  active: PropTypes.bool,
};

export default WorkOrderBody;
